import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { LockKey, CircleNotch, Eye, EyeSlash, CheckCircle, ShieldCheck, Warning } from "@phosphor-icons/react";

/**
 * ResetPasswordPage — public route /reset-password?token=...
 *
 * Validates the token on mount, then shows the new-password form.
 * Uses Commandix dark/orange branding (mirrors ForgotPasswordPage).
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [phase, setPhase] = useState("checking"); // "checking" | "form" | "done" | "invalid"
  const [reason, setReason] = useState("");
  const [emailHint, setEmailHint] = useState("");

  const [pw, setPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) { setPhase("invalid"); setReason("not_found"); return; }
      try {
        const r = await api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (r.data?.valid) {
          setEmailHint(r.data.email_hint || "");
          setPhase("form");
        } else {
          setReason(r.data?.reason || "invalid");
          setPhase("invalid");
        }
      } catch {
        if (!cancelled) { setPhase("invalid"); setReason("error"); }
      }
    }
    validate();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pwConfirm) { setError("Las contraseñas no coinciden"); return; }
    if (pw.length < 8) { setError("Mínimo 8 caracteres"); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw, confirm_password: pwConfirm });
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo actualizar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  const invalidMessage = {
    "not_found": "Enlace inválido o ya utilizado.",
    "already_used": "Este enlace ya fue utilizado. Solicita uno nuevo si necesitas restablecer tu contraseña.",
    "expired": "El enlace ha expirado. Solicita uno nuevo.",
    "error": "Ocurrió un error verificando el enlace.",
  }[reason] || "Enlace inválido.";

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12" style={{ background: "#0a0a0a" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ background: "rgba(234,138,40,0.12)", border: "1px solid rgba(234,138,40,0.25)" }}>
            <ShieldCheck size={28} weight="duotone" color="#ea8a28" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "#f5f5f5", letterSpacing: "-0.5px" }}>Commandix</h1>
          <p className="text-xs uppercase tracking-[0.2em] mt-1" style={{ color: "#9ca3af" }}>Restablecer contraseña</p>
        </div>

        <Card className="border" style={{ background: "#141414", borderColor: "rgba(255,255,255,0.08)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" style={{ color: "#f5f5f5" }}>
              {phase === "done" ? "Listo" : phase === "invalid" ? "Enlace inválido" : "Crear nueva contraseña"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {phase === "checking" && (
              <div className="py-8 text-center">
                <CircleNotch size={28} className="animate-spin inline" color="#ea8a28" />
                <p className="text-xs mt-3" style={{ color: "#9ca3af" }}>Validando enlace…</p>
              </div>
            )}

            {phase === "invalid" && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)" }}>
                  <Warning size={20} weight="fill" color="#f87171" className="shrink-0 mt-0.5" />
                  <div className="text-sm" style={{ color: "#f5f5f5" }}>
                    <p>{invalidMessage}</p>
                  </div>
                </div>
                <Button onClick={() => navigate("/forgot-password")}
                  className="w-full font-semibold"
                  style={{ background: "#ea8a28", color: "#0a0a0a" }}
                  data-testid="reset-request-new-btn">
                  Solicitar enlace nuevo
                </Button>
                <Link to="/login" className="block text-center text-xs hover:underline pt-1" style={{ color: "#9ca3af" }}>
                  Volver a iniciar sesión
                </Link>
              </div>
            )}

            {phase === "form" && (
              <form onSubmit={submit} className="space-y-4">
                {emailHint && (
                  <p className="text-xs" style={{ color: "#9ca3af" }}>
                    Restableciendo para: <span className="font-mono" style={{ color: "#f5f5f5" }}>{emailHint}</span>
                  </p>
                )}
                <p className="text-xs" style={{ color: "#9ca3af", lineHeight: 1.6 }}>
                  Mínimo 8 caracteres con al menos 1 letra y 1 número o símbolo.
                </p>

                <div className="space-y-1">
                  <label className="text-xs" style={{ color: "#9ca3af" }}>Nueva contraseña</label>
                  <div className="relative">
                    <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2" color="#6b7280" />
                    <Input
                      type={show ? "text" : "password"}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      className="pl-10 pr-10 border"
                      style={{ background: "#0a0a0a", borderColor: "rgba(255,255,255,0.08)", color: "#f5f5f5" }}
                      required
                      autoFocus
                      data-testid="reset-new-pw"
                    />
                    <button type="button" onClick={() => setShow(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      aria-label={show ? "Ocultar" : "Mostrar"}
                      data-testid="reset-pw-toggle">
                      {show ? <EyeSlash size={18} color="#9ca3af" /> : <Eye size={18} color="#9ca3af" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs" style={{ color: "#9ca3af" }}>Confirmar nueva contraseña</label>
                  <div className="relative">
                    <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2" color="#6b7280" />
                    <Input
                      type={show ? "text" : "password"}
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      className="pl-10 border"
                      style={{ background: "#0a0a0a", borderColor: "rgba(255,255,255,0.08)", color: "#f5f5f5" }}
                      required
                      data-testid="reset-confirm-pw"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm" style={{ color: "#f87171" }}>{error}</p>
                )}

                <Button type="submit" disabled={saving || !pw || !pwConfirm}
                  className="w-full font-semibold"
                  style={{ background: "#ea8a28", color: "#0a0a0a" }}
                  data-testid="reset-submit-btn">
                  {saving ? <CircleNotch size={16} className="mr-2 animate-spin" /> : null}
                  {saving ? "Guardando..." : "Cambiar contraseña"}
                </Button>
              </form>
            )}

            {phase === "done" && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)" }}>
                  <CheckCircle size={20} weight="fill" color="#34d399" className="shrink-0 mt-0.5" />
                  <div className="text-sm" style={{ color: "#f5f5f5" }}>
                    <p>Tu contraseña fue actualizada correctamente.</p>
                    <p className="mt-2 text-xs" style={{ color: "#9ca3af" }}>
                      Por seguridad, todas tus sesiones activas fueron cerradas.
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate("/login")}
                  className="w-full font-semibold"
                  style={{ background: "#ea8a28", color: "#0a0a0a" }}
                  data-testid="reset-go-login-btn">
                  Iniciar sesión
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] mt-6" style={{ color: "#6b7280" }}>
          Commandix Security · security@commandixcrm.com
        </p>
      </div>
    </div>
  );
}
